import React, { useRef, useState } from 'react'
import { CgImage, CgTrash, CgCopy } from 'react-icons/cg'
import { useNavigate } from 'react-router-dom'

import {
  useListenItemPreviewImgQuery,
  useListenProductImgQuery,
  useListenStripeResponseQuery,
  useSetOneTimeProductMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'
import { useSetExclusiveTabMutation, useSetTabContentMutation } from 'modules/Layout/layoutApi'
import { ONE_TIME_PRODUCT } from 'modules/App/components/Header'

import { getAppliedPromo, getPromoPrice } from 'modules/ProductInfo/utils/utils'

import { createUID } from 'common/utils/createUID'
import { Spinner } from 'common/components/Spinner/Spinner'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { CURRENCY_SYMBOL } from 'common/constants/currencyConstants'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { getFirebase, setFirebase } from 'modules/App/globalApi'
import { useListenPublishedCheckoutPageQuery } from 'modules/PageBuilder/pageBuilderApi'
import { getIsProductReadyForSale } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'
import { ShareLinkMenu } from 'modules/ProductInfo/components/ShareLinkMenu/ShareLinkMenu'
import { StatusPill } from 'modules/ProductInfo/components/StatusPill/StatusPill'
import { getOneTimeProductPagePath } from 'modules/ProductInfo/utils/shareLinks'

function ProductCard({ coachOrgId, product, currency, coupons, oneTimeProductSlugs, coachOrgSlug }) {
  const { createAlert } = useAlert()
  const navigate = useNavigate()

  const actionContainerRef = useRef()
  const slugRef = useRef()

  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [setOneTimeProduct] = useSetOneTimeProductMutation()
  const [setStripeRequest] = useSetStripeRequestMutation()
  const [setExclusiveTab] = useSetExclusiveTabMutation()
  const [setTabContent] = useSetTabContentMutation()

  const { data: itemImgData } = useListenItemPreviewImgQuery({
    coachOrgId,
    itemId: product?.item?.id,
    itemType: product?.item?.type,
  })
  const { data: productImgData } = useListenProductImgQuery({ coachOrgId, id: product?.id })
  const { data: publishedCheckoutPage } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId: product?.id })
  const publishedCheckoutPageLoading = publishedCheckoutPage === undefined || publishedCheckoutPage?.isLoading

  // Same gate as the product form: the link is shareable once the product is on sale. The
  // published checkout page is read only for legacy products, which have no readyForSale
  // flag and fall back to the older rule.
  const isReadyForSale = getIsProductReadyForSale({
    product,
    publishedCheckoutPage: publishedCheckoutPageLoading ? null : publishedCheckoutPage,
  })

  const { data: stripeResponse } = useListenStripeResponseQuery({ coachOrgId, responseId: product?.id })
  const stripeResponsePending = stripeResponse?.status === 'pending'
  const errorMessage = stripeResponse?.error

  const handleErrRetry = async () => {
    const payload = stripeResponse?.data?.payload
    const type = stripeResponse?.type
    const requestId = createUID()
    await setStripeRequest({
      requestId,
      coachOrgId,
      type,
      payload,
    })
    createAlert({ text: 'Retrying...', type: 'success' })
  }

  const handleDeleteProduct = async () => {
    setIsDeleting(true)
    await setStripeRequest({
      requestId: product?.id,
      coachOrgId,
      type: 'DELETE_ONE_TIME_PRODUCT',
      payload: {
        trybeProductId: product?.id,
        stripeProductId: product?.stripeProductId,
      },
    })
    await setOneTimeProduct({
      coachOrgId,
      product: null,
      productId: product?.id,
    })

    if (product?.item?.type === 'tab' && Boolean(product?.item?.id)) {
      setExclusiveTab({ coachOrgId, tabId: product.item.id, tab: null })
      setTabContent({ coachOrgId, tabId: product.item.id, tabContent: null })
    }
    setIsDeleting(false)
    createAlert({ text: 'Product deleted!', type: 'success' })
  }

  const handleDuplicateProduct = async (product) => {
    setIsDuplicating(true)

    const newProductId = createUID()
    const productName = `${product?.stripeProductName || ''} Copy`
    let defaultSlug = (productName || '').replace(/[\W_]+/g, '-').toLowerCase()
    if (oneTimeProductSlugs[defaultSlug]) {
      const slugUID = newProductId.replace(/[\W_]+/g, '-').toLowerCase()
      defaultSlug = `${defaultSlug}${slugUID}`
    }
    const hasTabItem = product?.item?.type === 'tab'
    const newTabId = createUID()
    const newProduct = {
      ...product,
      stripeProductName: productName,
      id: newProductId,
      createdAt: Date.now(),
      stripeProductId: null,
      stripe_price_id: null,
      siteCheckout: null,
      salesPage: null,
      readyForSale: false,
      price: product?.price,
      slug: defaultSlug,
      item: hasTabItem ? { ...product.item, id: newTabId } : product?.item || null,
    }

    await setOneTimeProduct({
      coachOrgId,
      product: newProduct,
      productId: newProductId,
    })
    createAlert({ text: 'Product duplicated!', type: 'success' })

    await setStripeRequest({
      requestId: newProductId,
      coachOrgId,
      type: 'CREATE_ONE_TIME_PRODUCT',
      payload: {
        trybeProductId: newProductId,
        name: productName,
        unitAmount: product?.price,
        currency,
      },
    })

    const { data: salesPage } = await getFirebase(`drafts/${coachOrgId}/oneTimeProducts/${product?.id}/salesPage`)
    await setFirebase(`drafts/${coachOrgId}/oneTimeProducts/${newProductId}/salesPage`, salesPage)
    const { data: checkoutPage } = await getFirebase(`drafts/${coachOrgId}/oneTimeProducts/${product?.id}/siteCheckout`)
    await setFirebase(`drafts/${coachOrgId}/oneTimeProducts/${newProductId}/siteCheckout`, checkoutPage)

    // copy sales and checkout draft pages
    if (hasTabItem) {
      const { data: tab } = await getFirebase(`coachOrgs/${coachOrgId}/explore/exclusiveTabs/${product?.item?.id}`)
      await setExclusiveTab({ coachOrgId, tabId: newTabId, tab })
      const { data: tabContent } = await getFirebase(`coachOrgs/${coachOrgId}/explore/tabContent/${product?.item?.id}`)
      await setTabContent({ coachOrgId, tabId: newTabId, tabContent })
    }

    setIsDuplicating(false)
  }

  const handleCardClick = (e) => {
    if (stripeResponsePending || errorMessage) {
      return
    }

    // Check that event did not happen in modal
    const occuredOutsideModal = e.currentTarget.contains(e.target)

    // Check that event did not happen in action container
    const occuredOutsideActionRow = !actionContainerRef.current?.contains(e.target)
    const occuredOutsideSlugText = !slugRef.current?.contains(e.target) && !e.target.closest?.('[data-share-trigger]')
    if (occuredOutsideModal && occuredOutsideActionRow && occuredOutsideSlugText) {
      navigate(`${ONE_TIME_PRODUCT}/${product?.id}`)
    }
  }

  // Published only when people can actually buy it: ready for sale, with a product link and the
  // coach's link handle, the same conditions the Share button needs
  const isLive = isReadyForSale && Boolean(product?.slug) && Boolean(coachOrgSlug)

  const isProductImgLoading = productImgData === undefined || productImgData?.isLoading
  const isItemImgLoading = itemImgData === undefined || itemImgData?.isLoading
  const itemImg = isItemImgLoading ? null : itemImgData
  const productImg = isProductImgLoading ? null : productImgData
  const image = productImg || itemImg

  const appliedPromo = getAppliedPromo({ product, coupons })
  const appliedCoupon = coupons?.[appliedPromo?.stripeCouponId]
  const isCouponApplied = product?.price >= 0 && appliedCoupon && appliedCoupon?.type !== 'trialOnly'

  return (
    <button
      onClick={handleCardClick}
      // The click is ignored until the backend answers the Stripe request, so say so.
      aria-busy={stripeResponsePending}
      className='relative group flex flex-col bg-white rounded-2xl cursor-pointer text-left border border-[#e6e8ec] transition-shadow hover:shadow-[0_8px_24px_rgba(28,34,64,0.08)]'
    >
      <div className='relative flex items-center gap-4 p-5'>
        <div className='relative w-20 h-20 rounded-xl overflow-hidden bg-tBlack flex-shrink-0'>
          {Boolean(image) ? (
            <img src={image} alt={product?.stripeProductName} className='w-full h-full object-cover' />
          ) : (
            <CgImage className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-50 w-7 h-7' />
          )}
        </div>
        <div className='flex-1 min-w-0'>
          <h4 className='text-lg font-semibold text-[#1c2240] capitalize truncate'>
            {product?.stripeProductName || 'Untitled Product'}
          </h4>
          <div className='flex items-center gap-2 text-base text-tGray-dark'>
            <span>
              {CURRENCY_SYMBOL[currency]}
              {isCouponApplied
                ? (getPromoPrice({ coupon: appliedCoupon, price: product?.price }) / 100).toFixed(2)
                : (product.price / 100).toFixed(2)}
            </span>
            {isCouponApplied && (
              <span className='text-sm line-through decoration-tRed'>
                {CURRENCY_SYMBOL[currency]}
                {(product.price / 100).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center justify-between gap-3 px-5 py-3 border-t border-[#eef0f3]'>
        <StatusPill label={isLive ? 'Published' : 'Draft'} tone={isLive ? 'live' : 'draft'} />
        <ShareLinkMenu
          tag='p'
          triggerRef={slugRef}
          coachSlug={coachOrgSlug}
          pagePath={getOneTimeProductPagePath({ slug: product?.slug, salesPage: product?.salesPage })}
          isReadyForSale={isReadyForSale && Boolean(product?.slug)}
          variant='button'
          setupHref={`${ONE_TIME_PRODUCT}/${product?.id}`}
        />
      </div>
      {Boolean(errorMessage) && (
        <div className='text-xs px-5 pb-3'>
          <span className='text-tRed'>{errorMessage}</span>
          <div
            className='inline-block ml-2 underline hover:text-tGreen transition-colors'
            onClick={() => handleErrRetry()}
          >
            Retry
          </div>
          <span className='mx-2'>or</span>
          <div
            className='inline-block underline hover:text-tRed transition-colors'
            onClick={() => handleDeleteProduct()}
          >
            Delete
          </div>
        </div>
      )}
      {!errorMessage && (
        <div
          ref={actionContainerRef}
          className='hidden group-hover:flex absolute right-2 top-2 items-center py-1 px-1.5 rounded-md border-[1px] bg-white border-gray-200 shadow-sm'
        >
          {stripeResponsePending || isDuplicating ? (
            <Spinner className='w-4 h-4 text-gray-100' />
          ) : (
            <>
              <Tooltip
                content='Duplicate'
                triggerClasses='mr-1 hidden group-hover:inline-block text-gray-500 hover:text-tGreen transition-all'
              >
                <CgCopy
                  className='w-[18px] h-[18px]'
                  aria-label={`Duplicate ${product?.stripeProductName || 'Untitled product'}`}
                  onClick={() => handleDuplicateProduct(product)}
                />
              </Tooltip>
              <Dialog open={deleteDialogOpen} setOpen={setDeleteDialogOpen}>
                <Tooltip
                  content='Delete'
                  triggerClasses='hidden group-hover:inline-block text-gray-500 hover:text-tRed transition-all'
                >
                  <DialogTrigger asChild={true} onOpenCb={(e) => e.stopPropagation()}>
                    <CgTrash className='w-5 h-5' />
                  </DialogTrigger>
                </Tooltip>
                <DialogContent>
                  <DeleteDialogBanner
                    text={`This will delete the ${product.stripeProductName || 'Untitled'} product`}
                    handleDelete={handleDeleteProduct}
                    typeToConfirm={true}
                    loading={isDeleting}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      )}
    </button>
  )
}

export default ProductCard
